import { memo, useCallback, useMemo } from 'react'
import { Box, MenuItem, Select, SelectChangeEvent, Stack, Typography } from '@mui/material'
import { AlertDrawing } from '../../../proto/generated/nexus'
import { alertModalMenuProps } from '../alertModal'
import { useStyles } from '../alertSettingsTab/styles'
import { buildFibonacciLevelMenuItems } from '../../../util/alerts'
import {
    FIBONACCI_ALERT_LOWER_LEVEL_OPTION_KEY,
    FIBONACCI_ALERT_UPPER_LEVEL_OPTION_KEY,
    getFibonacciChannelBoundLevelsFromOptions,
    validateFibonacciChannelBounds,
} from '../../../util/fibonacci'

type Props = {
    drawing: AlertDrawing
    onBoundsChange: (nextOptions: Record<string, string>) => void
    className?: string
    /** When false, render without the outer Condition-style row labels (nested in Condition value). */
    showRowLabels?: boolean
}

const FibonacciChannelBounds: React.FC<Props> = ({
    drawing,
    onBoundsChange,
    className,
    showRowLabels = true,
}) => {
    const { classes } = useStyles()
    const menuItems = useMemo(() => buildFibonacciLevelMenuItems(drawing), [drawing])
    const points = drawing.points ?? []
    const price1 = points[0]?.price
    const price2 = points[1]?.price

    const { upperLevel, lowerLevel } = useMemo(
        () => getFibonacciChannelBoundLevelsFromOptions(drawing.options, price1, price2),
        [drawing.options, price1, price2]
    )

    const validation = useMemo(
        () => validateFibonacciChannelBounds(price1, price2, upperLevel, lowerLevel),
        [price1, price2, upperLevel, lowerLevel]
    )

    const upperKey = String(upperLevel)
    const lowerKey = String(lowerLevel)

    const onUpperChange = useCallback(
        (event: SelectChangeEvent<string>) => {
            onBoundsChange({
                ...(drawing.options ?? {}),
                [FIBONACCI_ALERT_UPPER_LEVEL_OPTION_KEY]: event.target.value,
                [FIBONACCI_ALERT_LOWER_LEVEL_OPTION_KEY]: String(lowerLevel),
            })
        },
        [drawing.options, lowerLevel, onBoundsChange]
    )

    const onLowerChange = useCallback(
        (event: SelectChangeEvent<string>) => {
            onBoundsChange({
                ...(drawing.options ?? {}),
                [FIBONACCI_ALERT_UPPER_LEVEL_OPTION_KEY]: String(upperLevel),
                [FIBONACCI_ALERT_LOWER_LEVEL_OPTION_KEY]: event.target.value,
            })
        },
        [drawing.options, upperLevel, onBoundsChange]
    )

    const renderBoundRow = (
        label: string,
        value: string,
        onChange: (event: SelectChangeEvent<string>) => void
    ) => {
        const selectors = (
            <Stack direction="row" spacing={1} className={className}>
                <Select
                    size="small"
                    value="fibLevel"
                    disabled
                    fullWidth
                    sx={{ flex: 1 }}
                    MenuProps={alertModalMenuProps}
                >
                    <MenuItem value="fibLevel">Fib level</MenuItem>
                </Select>
                <Select
                    size="small"
                    value={menuItems.some((m) => m.key === value) ? value : menuItems[0]?.key ?? value}
                    onChange={onChange}
                    fullWidth
                    sx={{ flex: 1 }}
                    MenuProps={alertModalMenuProps}
                    error={!validation.valid}
                >
                    {menuItems.map((m) => (
                        <MenuItem key={m.key} value={m.key}>
                            {m.label}
                        </MenuItem>
                    ))}
                </Select>
            </Stack>
        )

        if (!showRowLabels) {
            return (
                <Box className={classes.row}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        {label}
                    </Typography>
                    {selectors}
                </Box>
            )
        }

        return (
            <Stack direction="row" className={classes.row}>
                <label className={classes.label}>{label}</label>
                <Box className={classes.value}>{selectors}</Box>
            </Stack>
        )
    }

    return (
        <Box>
            {renderBoundRow('Upper bound', upperKey, onUpperChange)}
            {renderBoundRow('Lower bound', lowerKey, onLowerChange)}
            {!validation.valid && validation.error && (
                <Typography
                    variant="caption"
                    color="error"
                    sx={{ display: 'block', mt: showRowLabels ? -1 : 0, mb: 1, ml: showRowLabels ? '30%' : 0 }}
                >
                    {validation.error}
                </Typography>
            )}
        </Box>
    )
}

export default memo(FibonacciChannelBounds)
